import { useCallback, useEffect, useState } from "react";
import { Check, Clock, AlertTriangle, Minus, Mail, ChevronDown, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

const STATE_STYLES = {
  done: { dot: "bg-[#4A7C59] text-white", box: "border-[#4A7C59]/30 bg-[#4A7C59]/5", Icon: Check },
  pending: { dot: "bg-[#E89B33] text-white", box: "border-[#E89B33]/40 bg-[#E89B33]/5", Icon: Clock },
  late: { dot: "bg-[#B54434] text-white", box: "border-[#B54434]/50 bg-[#B54434]/5", Icon: AlertTriangle },
  na: { dot: "bg-[#DCD8D0] text-[#8A8A8A]", box: "border-[#DCD8D0] bg-transparent opacity-60", Icon: Minus },
};

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const CHANNEL_LABELS = { email: "Email", whatsapp: "WhatsApp" };

function EmailRow({ e }) {
  const failed = e.status !== "sent";
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5 border-b border-[#EAE7E0] last:border-0 text-sm">
      <span className="w-28 shrink-0 text-xs text-[#8A8A8A]">{fmt(e.sent_at)}</span>
      <span className={`font-medium ${failed ? "text-[#B54434]" : "text-[#1A1A1A]"}`}>
        {e.label}
        {failed && " — falló"}
      </span>
      <span className="text-xs text-[#5C5C5C] break-all">
        {CHANNEL_LABELS[e.channel] || e.channel} → {e.to}
      </span>
      {failed && e.error && <span className="basis-full text-xs text-[#B54434]">{e.error}</span>}
    </div>
  );
}

// Barra de hitos de la reserva + correos enviados. Solo lee: el estado lo
// calcula el backend (progress.py) a partir de la reserva y su email_log.
export default function ReservationProgress({ reservation }) {
  const [t, setT] = useState(null);
  const id = reservation.id;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reservations/${id}/timeline`);
      setT(data);
    } catch {
      setT(null);
    }
  }, [id]);

  // `reservation` cambia de identidad cada vez que el detalle se recarga
  // (tras un cobro, una entrega…): se vuelve a pedir la barra.
  useEffect(() => {
    load();
  }, [load, reservation]);

  if (!t) return null;

  const pct = t.total ? Math.round((t.done / t.total) * 100) : 0;
  const clientEmails = t.emails.filter((e) => e.audience === "client");
  const ownerEmails = t.emails.filter((e) => e.audience === "owner");

  return (
    <section className="bg-white border border-[#DCD8D0] rounded-xl p-5 mb-6" data-testid="reservation-progress">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="text-overline">Progreso de la reserva</div>
        <div className="text-sm text-[#5C5C5C]">
          <span className="font-semibold text-[#1A1A1A]">{t.done}</span> de {t.total} pasos
          {t.late > 0 && (
            <span className="ml-2 font-semibold text-[#B54434]" data-testid="progress-late-count">
              · {t.late} con retraso
            </span>
          )}
          {t.whatsapp?.conversation_url && (
            <a
              href={t.whatsapp.conversation_url}
              target="_blank"
              rel="noreferrer"
              className="ml-3 inline-flex items-center gap-1 font-medium text-marca underline"
              data-testid="progress-whatsapp-link"
            >
              <MessageCircle className="h-3.5 w-3.5" /> Abrir conversación de WhatsApp
            </a>
          )}
        </div>
      </div>
      <div className="h-2 rounded-full bg-[#EAE7E0] overflow-hidden mb-4">
        <div className="h-full bg-[#4A7C59] transition-all" style={{ width: `${pct}%` }} />
      </div>

      <ol className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {t.steps.map((s, i) => {
          const st = STATE_STYLES[s.state] || STATE_STYLES.pending;
          const isNext = s.key === t.next;
          return (
            <li
              key={s.key}
              className={`flex gap-2.5 rounded-lg border px-3 py-2 ${st.box} ${isNext ? "ring-2 ring-marca/40" : ""}`}
              data-testid={`progress-step-${s.key}`}
              data-state={s.state}
            >
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${st.dot}`}>
                <st.Icon className="h-3 w-3" strokeWidth={3} />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium leading-tight text-[#1A1A1A]">
                  <span className="text-[#8A8A8A] font-normal">{i + 1}.</span> {s.label}
                </div>
                {s.at && <div className="text-xs text-[#5C5C5C]">{fmt(s.at)}</div>}
                {s.detail && <div className="text-xs text-[#5C5C5C] break-words">{s.detail}</div>}
                {s.channels?.length > 0 && (
                  <div className="text-[11px] text-[#5C5C5C]">
                    {s.channels.map((c) => CHANNEL_LABELS[c] || c).join(" + ")}
                  </div>
                )}
                {isNext && <div className="text-[11px] font-semibold uppercase tracking-wider text-marca mt-0.5">Siguiente paso</div>}
              </div>
            </li>
          );
        })}
      </ol>

      <details className="mt-4 group" data-testid="progress-emails">
        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-marca">
          <Mail className="h-4 w-4" />
          Emails enviados ({clientEmails.length} al cliente · {ownerEmails.length} avisos internos)
          <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
        </summary>
        <div className="mt-3 grid gap-4 lg:grid-cols-2">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-[#8A8A8A] mb-1">Al cliente</div>
            {clientEmails.length ? clientEmails.map((e, i) => <EmailRow key={i} e={e} />) : (
              <div className="text-sm text-[#8A8A8A]">Ninguno todavía</div>
            )}
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-[#8A8A8A] mb-1">Avisos internos (a ti)</div>
            {ownerEmails.length ? ownerEmails.map((e, i) => <EmailRow key={i} e={e} />) : (
              <div className="text-sm text-[#8A8A8A]">Ninguno todavía</div>
            )}
          </div>
        </div>
      </details>
    </section>
  );
}
